import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Clock, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NutritionIcons } from "./NutritionIcons";
import { Analytics } from "@/lib/analytics";

interface RecipeData {
  title: string;
  description?: string;
  servings: number;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  instructions: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber?: number;
  equipment?: string[];
  ingredients: {
    ingredient_name: string;
    quantity: number;
    unit: string;
  }[];
}

interface RecipeReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: () => void;
  onSkip?: () => void;
  recipeData: RecipeData | null;
  isImporting: boolean;
  currentIndex?: number;
  totalRecipes?: number;
}

export const RecipeReviewModal = ({
  isOpen,
  onClose,
  onImport,
  onSkip,
  recipeData,
  isImporting,
  currentIndex,
  totalRecipes
}: RecipeReviewModalProps) => {
  const { t } = useTranslation();
  if (!recipeData) return null;

  const totalTime = (recipeData.prep_time_minutes || 0) + (recipeData.cook_time_minutes || 0);
  const showCounter = totalRecipes && totalRecipes > 1;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Only react to close events; opening is controlled by parent state.
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-primary">
            {t('components.recipeReview.title')}
            {showCounter && (
              <span className="text-sm text-muted-foreground ml-2">
                {t('components.recipeReview.counter', { current: (currentIndex || 0) + 1, total: totalRecipes })}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Recipe Header */}
            <div>
              <h3 className="text-xl font-semibold mb-2">{recipeData.title}</h3>
              {recipeData.description && (
                <p className="text-muted-foreground">{recipeData.description}</p>
              )}
            </div>

            {/* Quick Info */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              {totalTime > 0 && (
                <div className="flex items-center gap-2 p-3 bg-secondary/20 rounded-lg">
                  <Clock className="h-4 w-4 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('components.recipeReview.time')}</p>
                    <p className="font-semibold">{t('components.recipeReview.minutesShort', { count: totalTime })}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 p-3 bg-secondary/20 rounded-lg">
                <Users className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">{t('components.recipeReview.servings')}</p>
                  <p className="font-semibold">{recipeData.servings}</p>
                </div>
              </div>
            </div>

            {/* Macros */}
            <div className="p-4 bg-gradient-card rounded-lg border border-border/50">
              <h4 className="font-semibold mb-3">{t('components.recipeReview.nutritionPerServing')}</h4>
              <NutritionIcons
                calories={recipeData.calories}
                protein={recipeData.protein}
                carbs={recipeData.carbs}
                fat={recipeData.fat}
                className="flex-wrap justify-center text-base"
                iconSize={16}
              />
            </div>

            {/* Ingredients */}
            <div>
              <h4 className="font-semibold mb-3">{t('components.recipeReview.ingredients')}</h4>
              <ul className="space-y-2">
                {recipeData.ingredients.map((ingredient, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      {ingredient.quantity} {ingredient.unit} {ingredient.ingredient_name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Equipment */}
            {recipeData.equipment && recipeData.equipment.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">{t('components.recipeReview.equipmentNeeded')}</h4>
                <ul className="space-y-2">
                  {recipeData.equipment.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Instructions */}
            <div>
              <h4 className="font-semibold mb-3">{t('components.recipeReview.instructions')}</h4>
              <div className="text-sm whitespace-pre-line leading-relaxed">
                {recipeData.instructions}
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => {
            Analytics.arnoldSuggestionDismissed('recipe');
            onClose();
          }} disabled={isImporting}>
            {t('common.cancel')}
          </Button>
          {onSkip && showCounter && (
            <Button 
              variant="outline" 
              onClick={() => {
                Analytics.arnoldSuggestionDismissed('recipe');
                onSkip?.();
              }} 
              disabled={isImporting}
              className="border-gold/30 hover:bg-gold/10"
            >
              {t('components.recipeReview.skipRecipe')}
            </Button>
          )}
          <Button 
            onClick={onImport} 
            disabled={isImporting}
            className="bg-gradient-primary text-black"
          >
            {isImporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.saving')}
              </>
            ) : showCounter ? (
              (currentIndex || 0) + 1 < (totalRecipes || 0)
                ? t('components.recipeReview.saveAndNext')
                : t('components.recipeReview.saveAndFinish')
            ) : (
              t('components.recipeReview.saveToRecipes')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
